<template>
    <ResourcePage v-if="guide" :theme="theme">
        <template #header>
            <ResourcePageHero :hide-image="hasForm || hasUngateButtonInHeader" :document="guide" />
        </template>
        <template #main>
            <SliceZone :components="components" :slices="guide?.data?.slices ?? []" :context="{parentDocument: guide}" />
        </template>
        <template #aside>
            <DownloadDoc v-if="hasUngateButtonInHeader" :document="guide" />
            <ResourcePageForm v-if="hasForm" :title="asText(guide.data?.formTitle)" v-bind="formConfig" />
            <TableOfContents v-else :document="guide" />
        </template>
    </ResourcePage>
</template>

<script lang="ts" setup>
    import {computed, ref} from 'vue';
    import {asText} from '@prismicio/helpers';
    import {SliceZone} from '@prismicio/vue';
    import type {GuideDocument} from '@prismicio-types';
    import {useRoute} from '@typed-router';
    import {useAsyncData, useWebsitePageView} from '#imports';
    import DownloadDoc from '~/components/DownloadDoc.vue';
    import ResourcePage from '~/components/ResourcePage.vue';
    import ResourcePageForm from '~/components/ResourcePageForm.vue';
    import ResourcePageHero from '~/components/ResourcePageHero.vue';
    import TableOfContents from '~/components/TableOfContents.vue';
    import usePrismicDocument from '~/composables/content/usePrismicDocument';
    import {useResolvePrismicDocument} from '~/composables/content/useResolvePrismicDocument';
    import {useDocumentMetadata} from '~/composables/useDocumentMetadata';
    import {components} from '~/slices/components';

    const theme = computed(() => {
        if (guide.value?.data.theme) {
            return guide.value?.data.theme;
        }
        if (guide.value?.data.useAlternativeHeroShapes) {
            return 'corporate';
        }
        if (guide.value?.data.maltStrategyTheme) {
            return 'strategy';
        }
        return 'default';
    });

    const {
        params: {uid},
    } = useRoute('resources-guide-uid');

    const {data, error} = await useAsyncData('guide', () => usePrismicDocument<GuideDocument>('guide', uid as string));
    const guide = useResolvePrismicDocument(data, error);

    const hasForm = ref(!!guide?.value?.data.hubspotFormId);
    const hasUngateButtonInHeader = computed(() => guide?.value?.data?.ungateButtonInHeader && !hasForm.value);

    const formConfig = ref({
        portalId: guide.value?.data.hubspotPortalId as string,
        formId: guide.value?.data.hubspotFormId as string,
        region: guide.value?.data.hubspotRegion as string,
        salesforceId: guide.value?.data.salesforceId as string,
        trackingCategory: 'resources-center-guides',
    });

    if (guide.value) {
        useDocumentMetadata(guide.value);
    }

    useWebsitePageView('resources-center', 'guides');
</script>
